import { openCartDrawer, refreshCartDrawerContents } from "~/core/cart-drawer";
import { initCart } from "~/core/cart-v2";
import {
    createRawItemPayload,
    type NameValuePair,
} from "~/core/cart-v2/create-raw-item-payload";
import { SingleItemPayload } from "~/core/cart-v2/operations/add-items/payload";
import type { EventNamespace } from "~/core/dom/events";
import { isHTMLFormElement } from "~/core/dom/guards";
import {
    clearButtonLoading,
    setButtonLoading,
} from "~/core/dom/loading-button";
import { findOneElement } from "~/core/dom/traversal";
import { initMainBus } from "~/core/messaging/main";

const CART_FORM_SELECTOR = 'form[action*="/cart/add"]';

export const bindAjaxCartForms = (
    namespace: EventNamespace,
    root: HTMLElement,
): void => {
    const Cart = initCart();
    const mainBus = initMainBus();
    const submittingForms = new WeakSet<HTMLFormElement>();

    namespace.addDelegatedEventListener(
        root,
        CART_FORM_SELECTOR,
        "submit",
        (form, evt) => {
            evt.preventDefault();

            if (typeof evt.stopImmediatePropagation === "function") {
                evt.stopImmediatePropagation();
            }

            if (!isHTMLFormElement(form) || submittingForms.has(form)) {
                return;
            }

            const submitButton = findOneElement(form, 'button[type="submit"]');

            const run = async (): Promise<void> => {
                submittingForms.add(form);
                setButtonLoading(submitButton);

                try {
                    const formDataNameValuePairs: NameValuePair[] = Array.from(
                        new FormData(form).entries(),
                    ).flatMap(([name, value]) =>
                        typeof value === "string" ? [{ name, value }] : [],
                    );
                    const itemPayload = await SingleItemPayload.parseAsync(
                        createRawItemPayload(formDataNameValuePairs),
                    );
                    const addItemsResult = await Cart.sendAsync({
                        type: "AddItems",
                        payload: {
                            items: [itemPayload],
                        },
                    });

                    if (addItemsResult === "busy") {
                        return;
                    }

                    await refreshCartDrawerContents().catch((err: unknown) => {
                        console.error(err);
                    });
                    openCartDrawer();
                    mainBus.send({
                        name: "request:open-cart-drawer",
                        details: null,
                        source: { type: "global" },
                    });
                } finally {
                    submittingForms.delete(form);
                    clearButtonLoading(submitButton);
                }
            };

            run().catch((err: unknown) => {
                console.error(err);
                submittingForms.delete(form);
                clearButtonLoading(submitButton);
            });
        },
        { capture: true },
    );
};
